import moment from 'moment';

import { UnitAlert } from '@mitsubishi/shared/data-access';

import { ProjectStatus } from '../enums';
import { Selection } from '../models';

export const SelectionAlertResources = {
  PRICE_VALIDITY_DATE_EXPIRED: '451556',
  /** Same text as the tooltip the consolidate button showed for an alternative without units */
  ALTERNATIVE_WITHOUT_UNITS: '445091',
  PRIVATE_PROJECT: '520992',
} as const;

export interface SelectionAlertsInput {
  /** Pure date "YYYY-MM-DD" (APPLYMESD-2803) */
  priceValidityDate: string | null | undefined;
  /** Today on the user's calendar, "YYYY-MM-DD" */
  today: string;
  someAlternativeWithoutUnits: boolean;
  isProjectPrivate: boolean;
}

/**
 * APPLYME-6315 (D-11): the reasons not tied to a single unit that keep the consolidation closed, computed client-side
 * from data the page already has. The price validity date is judged day against day on the user's calendar, as the
 * backend guard does.
 */
export function getSelectionAlerts(input: SelectionAlertsInput): UnitAlert[] {
  const alerts: UnitAlert[] = [];
  if (isPriceValidityExpired(input.priceValidityDate, input.today)) {
    alerts.push({ type: null, message: SelectionAlertResources.PRICE_VALIDITY_DATE_EXPIRED, parameters: [] });
  }
  if (input.someAlternativeWithoutUnits) {
    alerts.push({ type: null, message: SelectionAlertResources.ALTERNATIVE_WITHOUT_UNITS, parameters: [] });
  }
  if (input.isProjectPrivate) {
    alerts.push({ type: null, message: SelectionAlertResources.PRIVATE_PROJECT, parameters: [] });
  }
  return alerts;
}

/** Day against day: the price validity date ("YYYY-MM-DD") is before today ("YYYY-MM-DD", user's calendar) */
export function isPriceValidityExpired(
  priceValidityDate: string | null | undefined,
  today = moment().format('YYYY-MM-DD')
) {
  return !!priceValidityDate && priceValidityDate < today;
}

/** Some alternative without units: the condition the consolidate button already checked (empty base or alternative) */
export function hasAlternativeWithoutUnits(selection: Selection): boolean {
  return [selection.base, ...Object.values(selection.alternatives ?? {})].some(
    alternative => alternative?.unitCount === 0
  );
}

/** The selection alerts of the page, today taken on the user's calendar */
export function getSelectionAlertsOf(selection: Selection, projectStatus: ProjectStatus | undefined): UnitAlert[] {
  return getSelectionAlerts({
    priceValidityDate: selection.details.priceValidityDate,
    today: moment().format('YYYY-MM-DD'),
    someAlternativeWithoutUnits: hasAlternativeWithoutUnits(selection),
    isProjectPrivate: projectStatus === ProjectStatus.Private,
  });
}
